import React, { useState, useEffect, useRef } from 'react';
import '../components/Services.css';
import '../components/Contact.css';

const EventsPage = () => {
  const [selectedFilter, setSelectedFilter] = useState('All');
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [currentGallery, setCurrentGallery] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const allEvents = [
    {
      id: 1,
      title: 'SOS Bake Sale with EQOL',
      date: 'December 1, 2024',
      time: 'TBD',
      location: 'Earth Science',
      description: 'Join us for our upcoming bake sale event in collaboration with SOS. Support our mission while enjoying delicious treats!',
      category: 'Socials',
      image: '/images/events/bake-sale-placeholder.jpg', // Placeholder - you can upload actual image
      isPast: false,
      rsvpLink: '#' // Add your RSVP link here
    },
    {
      id: 2,
      title: 'General Meeting',
      date: 'November 21, 2024',
      time: '4:00 PM - 6:00 PM',
      location: 'Hunter Student Commons',
      description: 'Our main general meeting where the team came together to discuss initiatives, plan upcoming events, and strengthen our community. We played Bingo and had a great time connecting!',
      category: 'General',
      image: '/images/winner of bingo game, meet1.jpeg',
      gallery: [
        '/images/winner of bingo game, meet1.jpeg',
        '/images/group.jpeg',
        '/images/met1.png',
        '/images/met2.png',
        '/images/met3.png',
        '/images/met5.png',
        '/images/met6.png',
        '/images/met7.png',
        '/images/met8.png',
        '/images/met9.png',
        '/images/met10.jpeg',
        '/images/met12.jpeg'
      ],
      isPast: true,
      rsvpLink: null
    }
  ];

  const filters = ['All', 'Workshops', 'Socials', 'Volunteering', 'General'];

  const filteredEvents = allEvents.filter(event => {
    const matchesFilter = selectedFilter === 'All' || event.category === selectedFilter;
    const matchesTimeframe = showPastEvents ? event.isPast : !event.isPast;
    return matchesFilter && matchesTimeframe;
  });

  const handleRSVP = (event, e) => {
    e.preventDefault();
    if (event.rsvpLink && event.rsvpLink !== '#') {
      window.open(event.rsvpLink, '_blank');
    } else {
      // You can add a modal or form here
      alert('RSVP link will be available soon!');
    }
  };

  const openGallery = (event) => {
    if (event.gallery && event.gallery.length > 0) {
      setCurrentGallery(event.gallery);
      setCurrentImageIndex(0);
      setShowGallery(true);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }
  };

  const closeGallery = () => {
    setShowGallery(false);
    document.body.style.overflow = 'auto'; // Restore scrolling
  };

  const nextImage = () => {
    if (currentGallery && currentGallery.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % currentGallery.length);
    }
  };

  const prevImage = () => {
    if (currentGallery && currentGallery.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + currentGallery.length) % currentGallery.length);
    }
  };

  useEffect(() => {
    if (!showGallery || !currentGallery || currentGallery.length === 0) return;

    const handleKeyPress = (e) => {
      if (e.key === 'ArrowRight') {
        setCurrentImageIndex((prev) => (prev + 1) % currentGallery.length);
      }
      if (e.key === 'ArrowLeft') {
        setCurrentImageIndex((prev) => (prev - 1 + currentGallery.length) % currentGallery.length);
      }
      if (e.key === 'Escape') {
        closeGallery();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [showGallery, currentGallery.length]);

  return (
    <div className="page-container">
      <section className="services section" ref={sectionRef}>
        <div className="container">
          <div className={`services-header ${isVisible ? 'animate-fade-in' : ''}`}>
            <h1 className="section-title">Events</h1>
            <p className="section-subtitle">
              Join us for workshops, socials, volunteering opportunities, and community gatherings.
            </p>
          </div>

          {/* Filter and Toggle Section */}
          <div className={`events-controls ${isVisible ? 'animate-fade-in' : ''}`}>
            <div className="events-toggle">
              <button
                className={`toggle-btn ${!showPastEvents ? 'active' : ''}`}
                onClick={() => setShowPastEvents(false)}
              >
                Upcoming Events
              </button>
              <button
                className={`toggle-btn ${showPastEvents ? 'active' : ''}`}
                onClick={() => setShowPastEvents(true)}
              >
                Past Events
              </button>
            </div>

            <div className="events-filters">
              {filters.map(filter => (
                <button
                  key={filter}
                  className={`filter-btn ${selectedFilter === filter ? 'active' : ''}`}
                  onClick={() => setSelectedFilter(filter)}
                >
                  {filter}
                </button>
              ))}
            </div>
          </div>

          {/* Events Grid */}
          {filteredEvents.length > 0 ? (
            <div className="events-grid">
              {filteredEvents.map((event, index) => (
                <div
                  key={event.id}
                  className={`event-card ${isVisible ? 'animate-fade-in' : ''}`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="event-image-container">
                    <img
                      src={event.image}
                      alt={event.title}
                      className="event-image"
                      onError={(e) => {
                        // If image fails to load, show a simple colored placeholder
                        e.target.style.display = 'none';
                        const container = e.target.parentElement;
                        if (!container.querySelector('.image-placeholder')) {
                          const placeholder = document.createElement('div');
                          placeholder.className = 'image-placeholder';
                          placeholder.innerHTML = '<svg width="60" height="60" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/><path d="M3 9L12 15L21 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>';
                          container.appendChild(placeholder);
                        }
                      }}
                    />
                    {event.isPast && (
                      <div className="event-badge past">Past Event</div>
                    )}
                    {!event.isPast && (
                      <div className="event-badge upcoming">Upcoming</div>
                    )}
                    {event.gallery && event.gallery.length > 1 && (
                      <div 
                        className="event-gallery-indicator"
                        onClick={(e) => {
                          e.stopPropagation();
                          openGallery(event);
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
                          <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
                          <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
                          <rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        <span>{event.gallery.length} photos</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="event-content">
                    <div className="event-category">{event.category}</div>
                    <h3 className="event-title">{event.title}</h3>
                    
                    <div className="event-details">
                      <div className="event-detail-item">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M8 2V6M16 2V6M3 10H21M5 4H19C20.1 4 21 4.9 21 6V20C21 21.1 20.1 22 19 22H5C3.9 22 3 21.1 3 20V6C3 4.9 3.9 4 5 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>{event.date}</span>
                      </div>
                      
                      {event.time && (
                        <div className="event-detail-item">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                            <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                          <span>{event.time}</span>
                        </div>
                      )}
                      
                      <div className="event-detail-item">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M21 10C21 17 12 23 12 23S3 17 3 10C3 7.6 4.1 5.3 6 3.7C7.9 2.1 10.3 1 12.8 1C15.3 1 17.7 2.1 19.6 3.7C21.5 5.3 22.6 7.6 22.6 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>{event.location}</span>
                      </div>
                    </div>
                    
                    <p className="event-description">{event.description}</p>
                    
                    {!event.isPast && event.rsvpLink && (
                      <button
                        className="btn btn-primary event-rsvp-btn"
                        onClick={(e) => handleRSVP(event, e)}
                      >
                        RSVP
                      </button>
                    )}
                    {event.gallery && event.gallery.length > 1 && (
                      <button
                        className="btn btn-secondary event-gallery-btn"
                        onClick={() => openGallery(event)}
                      >
                        View Gallery ({event.gallery.length} photos)
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-events-message">
              <p>No events found for the selected filters.</p>
            </div>
          )}
        </div>
      </section>

      {/* Gallery Slideshow Modal */}
      {showGallery && (
        <div className="gallery-modal" onClick={closeGallery}>
          <div className="gallery-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="gallery-close-btn" onClick={closeGallery}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            
            <div className="gallery-main-image">
              <button className="gallery-nav-btn gallery-prev" onClick={prevImage}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              
              <img
                src={currentGallery[currentImageIndex]}
                alt={`Gallery image ${currentImageIndex + 1}`}
                className="gallery-image"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              
              <button className="gallery-nav-btn gallery-next" onClick={nextImage}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            <div className="gallery-thumbnails">
              {currentGallery.map((img, index) => (
                <img
                  key={index}
                  src={img}
                  alt={`Thumbnail ${index + 1}`}
                  className={`gallery-thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                  onClick={() => setCurrentImageIndex(index)}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ))}
            </div>

            <div className="gallery-counter">
              {currentImageIndex + 1} / {currentGallery.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsPage;

